import React, { useState } from "react"

import styles from "./importMappingResult.module.css"
import { getExpectedColumns } from "../../../../utils/importColumns"

// Visão "Importação Inteligente — Mapeamento": como as colunas do arquivo entram no modelo do sistema.
// Um cartão por tipo. Modo read-only (consulta) ou editável (revisão: escolher a coluna de cada campo).

const IconCheck = ({ size = 13, stroke = "currentColor", w = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
)
const IconArrowLeft = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 5l-7 7 7 7" /></svg>
)
const IconAlertCircle = ({ size = 13, stroke = "currentColor", w = 2.6 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
)
const IconDatabase = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 3 6 3s6-2 6-3v-5" /></svg>
)
const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5f7488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
)
const IconChevron = ({ up }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform .15s", transform: up ? "rotate(180deg)" : "none" }}><path d="M6 9l6 6 6-6" /></svg>
)

const fmtCount = (n) => (typeof n === "number" ? n.toLocaleString("pt-BR") : n)

const SYSTEM_FIELD_LABELS = {
  AccountReceiveStatus: "Situação da conta",
  BillType: "Tipo de boleto"
}
const systemFieldLabel = (sf) => sf.label || SYSTEM_FIELD_LABELS[sf.field] || sf.field

function SourceSelect({ value, options, placeholder, disabled, onChange, className }) {
  return (
    <select
      className={`${styles.rowSelect}${className ? ` ${className}` : ""}`}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o, i) => (
        <option key={i} value={o}>{o}</option>
      ))}
    </select>
  )
}

function EntityCard({
  d, typeMap, receivedHeaders, sampleRows, rowCount, fileName,
  editable, isSelected, onToggleType, onMapField, onRemoveType
}) {
  const [filter, setFilter] = useState("todos")
  const [collapsed, setCollapsed] = useState(false)

  const label = d.label || d.importType
  const templateColsRaw = getExpectedColumns(d.importType)
  const templateCols = templateColsRaw.length > 0 ? templateColsRaw : Object.keys(typeMap)
  const missingKeyFields = d.missingKeyFields || []

  const sampleFor = (fileCol) => {
    for (const row of sampleRows) {
      const v = row?.[fileCol]
      if (v != null && String(v).trim() !== "") {
        return String(v)
      }
    }
    return "—"
  }

  // Campos preenchidos por INFERÊNCIA (ex.: período do nome do arquivo, §12.G): não vêm de coluna,
  // mas foram gravados nos registros. constantFields = { canônico: valor }.
  const constantFields = d.constantFields || {}
  const hasConstant = (c) => {
    const v = constantFields[c]
    return v != null && String(v).trim() !== ""
  }

  const mappedList = templateCols
    .filter((c) => typeMap[c])
    .map((c) => ({ sys: c, src: typeMap[c], sample: sampleFor(typeMap[c]) }))
  const constantList = templateCols
    .filter((c) => !typeMap[c] && hasConstant(c))
    .map((c) => ({ sys: c, value: String(constantFields[c]) }))
  const unmappedList = templateCols
    .filter((c) => !typeMap[c] && !hasConstant(c))
    .map((c) => ({ sys: c, important: missingKeyFields.includes(c) }))

  const total = templateCols.length
  // "Resolvidos" = mapeados de coluna + aplicados por inferência (ambos têm valor no registro).
  const mappedCount = mappedList.length + constantList.length
  const unmappedCount = unmappedList.length
  // "Importantes faltando" = chave natural ainda sem coluna (recalcula ao vivo no modo editável).
  const importantFields = unmappedList.filter((f) => f.important)
  const importantCount = importantFields.length

  const showMapped = filter !== "nao_mapeados"
  const showUnmapped = filter !== "mapeados"

  const mapsToFor = (header) =>
    Object.keys(typeMap).filter((canonical) => typeMap[canonical] === header)
  const previewRows = sampleRows.slice(0, 10)
  const disableEdit = editable && !isSelected

  // Mapeamento manual: escolher, no cabeçalho da coluna do arquivo, qual campo do sistema ela alimenta.
  // Valor vazio remove o campo que hoje aponta para esta coluna. handleHeaderMapping garante 1 coluna→1 campo.
  const onHeaderMap = (fileCol, canonical) => {
    const current = mapsToFor(fileCol)[0]
    if (canonical) {
      onMapField(d.importType, canonical, fileCol)
    } else if (current) {
      onMapField(d.importType, current, "")
    }
  }

  const Tab = ({ value, children }) => (
    <button
      type="button"
      className={`${styles.tab} ${filter === value ? styles.tabActive : ""}`}
      onClick={() => { setFilter(value); setCollapsed(false) }}
    >
      {children}
    </button>
  )

  return (
    <div className={`${styles.card} ${editable && !isSelected ? styles.cardDisabled : ""}`}>
      {/* header */}
      <div className={styles.cardHead}>
        {editable && (
          <input
            type="checkbox"
            className={styles.includeCheck}
            checked={isSelected}
            onChange={() => onToggleType(d.importType)}
            title="Incluir este tipo na importação"
          />
        )}
        <div className={styles.cardIcon}><IconDatabase /></div>
        <div className={styles.cardHeadInfo}>
          <div className={styles.entityName}>{label}</div>
          <div className={styles.cardMeta}>
            {fileName && <span className={styles.metaMono}>{fileName}</span>}
            {fileName && typeof rowCount !== "undefined" && rowCount !== null && (
              <span className={styles.metaDot}>•</span>
            )}
            {typeof rowCount !== "undefined" && rowCount !== null && (
              <span><strong className={styles.metaStrong}>{fmtCount(rowCount)}</strong> linhas</span>
            )}
          </div>
        </div>
        <div className={styles.cardPills}>
          {d.naturalKeySatisfied ? (
            <span className={`${styles.pill} ${styles.pillKeyOk}`}>
              <IconCheck stroke="#2e9e5b" w={2.4} /> Chave completa
            </span>
          ) : (
            <span className={`${styles.pill} ${styles.pillKeyWarn}`}>
              <IconAlertCircle stroke="#d98a1f" w={2.2} /> Chave incompleta
            </span>
          )}
          {d.manual ? (
            <span className={`${styles.pill} ${styles.pillManual}`}>Manual</span>
          ) : typeof d.confidence === "number" && d.confidence > 0 ? (
            <span className={`${styles.pill} ${styles.pillConf}`}>
              Confiança da IA {Math.round(d.confidence * 100)}%
            </span>
          ) : null}
          {editable && d.manual && (
            <button
              type="button"
              className={styles.removeTypeBtn}
              onClick={() => onRemoveType(d.importType)}
              title="Remover tipo"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* stat tiles */}
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <div className={styles.tileBar} />
          <div className={styles.tileNum}>{total}</div>
          <div className={styles.tileLabel}>Campos no modelo</div>
        </div>
        <div className={`${styles.tile} ${styles.tileGreen}`}>
          <div className={styles.tileBar} />
          <div className={styles.tileNum}>{mappedCount}<span className={styles.tileOf}>de {total}</span></div>
          <div className={styles.tileLabel}><IconCheck stroke="#2e9e5b" w={2.4} /> Mapeados</div>
        </div>
        <div className={`${styles.tile} ${styles.tileAmber}`}>
          <div className={styles.tileBar} />
          <div className={styles.tileNum}>{unmappedCount}</div>
          <div className={styles.tileLabel}><IconAlertCircle stroke="#d98a1f" w={2.2} /> Sem mapeamento</div>
        </div>
        <div className={`${styles.tile} ${styles.tileRed}`}>
          <div className={styles.tileBar} />
          <div className={styles.tileNum}>{importantCount}</div>
          <div className={styles.tileLabel}><IconAlertCircle stroke="#c0392b" w={2.2} /> Importantes faltando</div>
        </div>
      </div>

      {/* important-missing alert */}
      {importantCount > 0 && (
        <div className={styles.important}>
          <div className={styles.importantHead}>
            <span className={styles.importantIcon}><IconAlertCircle size={17} stroke="#c0392b" w={2.2} /></span>
            <div>
              <div className={styles.importantTitle}>Campos importantes não mapeados</div>
              <div className={styles.importantSub}>
                {editable
                  ? "Campos-chave do modelo sem coluna correspondente. Mapeie-os na tabela de comparação abaixo, pelo cabeçalho da coluna do arquivo — sem eles os registros entram sempre como novos (risco de duplicação)."
                  : "Estes campos-chave do modelo não têm coluna correspondente no arquivo — registros podem ser inseridos como novos (risco de duplicação em reimportações)."}
              </div>
            </div>
          </div>
          <div className={styles.importantGrid}>
            {importantFields.map((f) => (
              <div key={f.sys} className={styles.importantItem}>
                <div className={styles.importantItemHead}>
                  <span className={styles.importantSys}>{f.sys}</span>
                  <span className={styles.reqBadge}>OBRIGATÓRIO</span>
                </div>
                <div className={styles.importantImpact}>Faz parte da chave natural que identifica cada registro.</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* campos preenchidos pelo sistema */}
      {d.systemFields?.length > 0 && (
        <div className={styles.systemFields}>
          <div className={styles.systemFieldsTitle}>Preenchido automaticamente pelo sistema</div>
          <ul className={styles.systemFieldsList}>
            {d.systemFields.map((sf, i) => (
              <li key={i}>
                <span className={styles.systemFieldName}>{systemFieldLabel(sf)}:</span> <strong>{sf.value}</strong>
                {sf.note ? ` — ${sf.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* mapping list */}
      <div className={styles.body}>
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            <Tab value="todos">Todos ({total})</Tab>
            <Tab value="mapeados">Mapeados ({mappedCount})</Tab>
            <Tab value="nao_mapeados">Não mapeados ({unmappedCount})</Tab>
          </div>
          <div className={styles.toolbarRight}>
            <div className={styles.legend}>
              <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotGreen}`} />Mapeado</span>
              <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotAmber}`} />Sem correspondência</span>
            </div>
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? "Expandir mapeamento" : "Recolher mapeamento"}
              aria-expanded={!collapsed}
            >
              <IconChevron up={!collapsed} />
            </button>
          </div>
        </div>

        {!collapsed && (<>
        {showMapped && mappedCount > 0 && (
          <div className={styles.mapSection}>
            <div className={styles.mapHead}>
              <span />
              <span>Campo do sistema</span>
              <span />
              <span>Coluna do arquivo</span>
              <span>Exemplo</span>
            </div>
            {mappedList.map((f) => (
              <div key={f.sys} className={styles.mapRow}>
                <span className={styles.checkDot}><IconCheck stroke="#2e9e5b" w={3} /></span>
                <span className={styles.sysField}>{f.sys}</span>
                <span className={styles.arrowCell}><IconArrowLeft /></span>
                <span className={styles.srcChip} title={f.src}>{f.src}</span>
                <span className={styles.sampleCell} title={f.sample}>{f.sample}</span>
              </div>
            ))}
            {constantList.map((f) => (
              <div key={f.sys} className={styles.mapRow}>
                <span className={`${styles.checkDot} ${styles.checkDotConst}`}><IconCheck stroke="#2565a8" w={3} /></span>
                <span className={styles.sysField}>{f.sys}</span>
                <span className={styles.arrowCell}><IconArrowLeft /></span>
                <span className={styles.constChip} title="Aplicado automaticamente (período identificado no arquivo)">período aplicado</span>
                <span className={styles.sampleCell} title={f.value}>{f.value}</span>
              </div>
            ))}
          </div>
        )}

        {showUnmapped && unmappedCount > 0 && (
          <div className={styles.unmapSection}>
            <div className={styles.unmapHead}>
              <span />
              <span>Campo do sistema</span>
              <span>Situação</span>
              <span style={{ textAlign: "right" }}>Prioridade</span>
            </div>
            {unmappedList.map((f) => (
              <div key={f.sys} className={styles.unmapRow}>
                <span className={`${styles.warnDot} ${f.important ? styles.warnDotReq : styles.warnDotOpt}`}>
                  <IconAlertCircle stroke={f.important ? "#c0392b" : "#d98a1f"} w={2.6} />
                </span>
                <span className={`${styles.unmapName} ${f.important ? styles.unmapNameReq : styles.unmapNameOpt}`}>{f.sys}</span>
                <span className={styles.situacao}>
                  <span className={styles.noMatchChip}>— sem correspondência —</span>
                </span>
                <span className={`${styles.prioBadge} ${f.important ? styles.prioReq : styles.prioOpt}`}>
                  {f.important ? "OBRIGATÓRIO" : "OPCIONAL"}
                </span>
              </div>
            ))}
          </div>
        )}
        </>)}

        {/* side-by-side data comparison */}
        {previewRows.length > 0 && receivedHeaders.length > 0 && (
          <div className={styles.compare}>
            <div className={styles.compareHead}>
              <div className={styles.compareTitle}>Comparação de dados</div>
              <div className={styles.compareHint}>Amostra das primeiras {previewRows.length} linha(s) — role na horizontal para ver todas as colunas.</div>
            </div>
            <div className={styles.compareGrid}>
              {/* arquivo importado */}
              <div className={styles.dataPanel}>
                <div className={styles.dataPanelHead}>
                  <div className={styles.dataPanelLabel}>ARQUIVO IMPORTADO</div>
                  <div className={`${styles.dataPanelSub} ${styles.dataPanelSubMono}`}>
                    {fileName || "arquivo"}{typeof rowCount === "number" ? ` — ${fmtCount(rowCount)} linha(s)` : ""}
                  </div>
                </div>
                <div className={styles.dataScroll}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th className={styles.thIndex}>#</th>
                        {receivedHeaders.map((h, i) => {
                          const mapsTo = mapsToFor(h)
                          return (
                            <th key={i}>
                              <div className={styles.colName}>{h}</div>
                              {editable ? (
                                <SourceSelect
                                  className={styles.headSelect}
                                  value={mapsTo[0] || ""}
                                  options={templateCols}
                                  placeholder="— mapear campo —"
                                  disabled={disableEdit}
                                  onChange={(canonical) => onHeaderMap(h, canonical)}
                                />
                              ) : (
                                mapsTo.length > 0 && <div className={styles.colSub}>→ {mapsTo.join(", ")}</div>
                              )}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, ri) => (
                        <tr key={ri}>
                          <td className={styles.tdIndex}>{ri + 1}</td>
                          {receivedHeaders.map((h, ci) => (
                            <td key={ci}>{row?.[h] != null && String(row[h]).trim() !== "" ? String(row[h]) : "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* template do sistema */}
              <div className={styles.dataPanel}>
                <div className={styles.dataPanelHead}>
                  <div className={styles.dataPanelLabel}>TEMPLATE DO SISTEMA — {label.toUpperCase()}</div>
                  <div className={styles.dataPanelSub}>
                    {total} colunas esperadas <strong style={{ color: "#b83224", fontWeight: 800 }}>({unmappedCount} sem mapeamento)</strong>
                  </div>
                </div>
                <div className={styles.dataScroll}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th className={styles.thIndex}>#</th>
                        {templateCols.map((col, i) => {
                          const src = typeMap[col]
                          const mapped = !!src
                          const isConst = !mapped && hasConstant(col)
                          const important = missingKeyFields.includes(col)
                          const headCls = mapped ? "" : isConst ? styles.headConst : important ? styles.headReq : styles.headOpt
                          const nameCls = mapped ? styles.colName : isConst ? styles.colNameConst : important ? styles.colNameReq : styles.colNameOpt
                          const subCls = mapped ? styles.colSub : isConst ? styles.colSubConst : important ? styles.colSubReq : styles.colSubOpt
                          return (
                            <th key={i} className={headCls}>
                              <div className={nameCls}>{col}</div>
                              <div className={subCls}>{mapped ? `← ${src}` : isConst ? "← período aplicado" : important ? "← sem origem (obrigatório)" : "← sem origem"}</div>
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, ri) => (
                        <tr key={ri}>
                          <td className={styles.tdIndex}>{ri + 1}</td>
                          {templateCols.map((col, ci) => {
                            const src = typeMap[col]
                            const mapped = !!src
                            const isConst = !mapped && hasConstant(col)
                            const important = missingKeyFields.includes(col)
                            const val = mapped
                              ? (row?.[src] != null && String(row[src]).trim() !== "" ? String(row[src]) : "—")
                              : isConst ? String(constantFields[col]) : "—"
                            const cellCls = mapped ? "" : isConst ? styles.cellConst : important ? styles.cellUnmappedReq : styles.cellUnmappedOpt
                            return <td key={ci} className={cellCls}>{val}</td>
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function MappingResultView({
  types, mappings, receivedHeaders, sampleRows, rowCount, fileName,
  editable = false, selected, onToggleType, onMapField, onRemoveType
}) {
  return (
    <div className={styles.view}>
      <div className={styles.intro}>
        <p className={styles.introText}>
          {editable
            ? "Confira e ajuste como as colunas do arquivo entram no modelo do sistema — "
            : "Como a IA organizou as colunas do seu arquivo dentro do modelo do sistema — "}
          <strong className={styles.introStrong}>campo do sistema ← coluna do arquivo</strong>.
        </p>
        {!editable && <span className={styles.roPill}><IconLock /> Somente leitura</span>}
      </div>

      {types.map((d) => (
        <EntityCard
          key={d.importType}
          d={d}
          typeMap={mappings[d.importType] || d.fieldMapping || {}}
          receivedHeaders={receivedHeaders}
          sampleRows={sampleRows}
          rowCount={rowCount}
          fileName={fileName}
          editable={editable}
          isSelected={selected ? !!selected[d.importType] : true}
          onToggleType={onToggleType}
          onMapField={onMapField}
          onRemoveType={onRemoveType}
        />
      ))}
    </div>
  )
}
